/**
 * 认证 API 模块
 *
 * 提供登录、登出、获取用户信息、修改密码等认证相关接口
 * 所有接口使用 Sa-Token 认证，token 通过 header 传递
 */
import { post, get } from './platform-client'
import type { UserInfo } from '@/stores/auth-store'

/* ============================================
 * 类型定义
 * ============================================ */

/** 登录请求参数 */
export interface LoginRequest {
  /** 用户名 */
  username: string
  /** 密码 */
  password: string
  /** 滑动验证码凭证 */
  captchaVerification?: string
}

/** 登录响应数据（后端 AdminLoginRes 结构） */
export interface LoginResponse {
  /** 管理员ID */
  adminId: string
  /** 用户名 */
  username: string
  /** 真实姓名 */
  realName: string
  /** 邮箱 */
  email?: string
  /** 认证令牌 */
  token: string
  /** 角色名称 */
  roleName?: string
  /** 角色编码 */
  roleCode?: string
  /** 权限列表 */
  permissions?: string[]
  /** 可切换的业务线列表（后端按角色菜单计算，不含平台级 platform） */
  allowedBizLines?: string[]
  /** 是否需要短信二次验证（true 时不返回 token，凭 ticket 走 verify2fa 换 token） */
  twoFactor?: boolean
  /** 二次验证票据（一次性，5 分钟有效） */
  ticket?: string
  /** 脱敏手机号（138****1234） */
  mobileMasked?: string
}

/** 短信二次验证换 token 请求参数 */
export interface TwoFactorVerifyRequest {
  /** 登录响应返回的一次性票据 */
  ticket: string
  /** 短信验证码 */
  code: string
}

/** 修改密码请求参数 */
export interface ChangePasswordRequest {
  /** 原密码 */
  oldPassword: string
  /** 新密码 */
  newPassword: string
}

/** 用户信息响应 */
export type UserInfoResponse = UserInfo

/* ============================================
 * API 接口
 * ============================================ */

/**
 * 用户登录
 *
 * @param data - 登录参数
 * @returns 登录响应，包含 token 和用户信息
 *
 * @example
 * const result = await login({ username: 'admin', password: '123456' })
 * console.log(result.token, result.user)
 */
export async function login(data: LoginRequest): Promise<LoginResponse> {
  return post<LoginResponse>('/auth/login', data)
}

/**
 * 重发登录短信验证码（凭一次性票据；后端 50 秒频控）
 *
 * @param ticket - 登录响应返回的一次性票据
 */
export async function send2faCode(ticket: string): Promise<void> {
  return post<void>('/auth/2fa/send', { ticket })
}

/**
 * 校验登录短信验证码，换取正式 token
 *
 * @param data - 票据 + 验证码
 * @returns 登录响应，包含 token 和用户信息
 */
export async function verify2fa(data: TwoFactorVerifyRequest): Promise<LoginResponse> {
  return post<LoginResponse>('/auth/2fa/verify', data)
}

/**
 * 用户登出
 *
 * 清除服务器端的会话状态
 *
 * @example
 * await logout()
 */
export async function logout(): Promise<void> {
  return get<void>('/auth/logout')
}

/**
 * 获取当前登录用户信息
 *
 * @returns 用户信息
 *
 * @example
 * const userInfo = await getCurrentUser()
 * console.log(userInfo.username, userInfo.roles)
 */
export async function getCurrentUser(): Promise<UserInfoResponse> {
  return post<UserInfoResponse>('/auth/info')
}

/**
 * 修改密码
 *
 * @param data - 修改密码参数
 * @returns 修改结果
 *
 * @example
 * await changePassword({
 *   oldPassword: 'old123',
 *   newPassword: 'new456'
 * })
 */
export async function changePassword(data: ChangePasswordRequest): Promise<void> {
  return post<void>('/auth/change-password', data)
}

/* ============================================
 * 导出
 * ============================================ */

export const authApi = {
  login,
  logout,
  getCurrentUser,
  changePassword,
}

export default authApi